import React, { useState } from 'react';
import Layout from '../components/Layout';
import { supabase } from '../utils/supabase';

const HashGeneratorPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [salt, setSalt] = useState('');
  const [customSalt, setCustomSalt] = useState('');
  const [useCustomSalt, setUseCustomSalt] = useState(false);
  const [hash, setHash] = useState('');
  const [copied, setCopied] = useState(false);
  const [verifyFromDB, setVerifyFromDB] = useState('');
  const [dbMatch, setDbMatch] = useState<{ user: string; match: boolean } | null>(null);

  const generateSalt = (): string => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const hashPassword = async (password: string, salt: string): Promise<string> => {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits']
    );
    const bits = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt: encoder.encode(salt), iterations: 100000, hash: 'SHA-256' },
      keyMaterial,
      256
    );
    const hashArray = Array.from(new Uint8Array(bits));
    return salt + ':' + hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleGenerate = async () => {
    if (!password.trim()) return;
    
    const saltToUse = useCustomSalt && customSalt.trim() ? customSalt.trim() : generateSalt();
    const newHash = await hashPassword(password, saltToUse);
    setSalt(saltToUse);
    setHash(newHash);
    setCopied(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerifyFromDB = async (table: 'user_master' | 'user_access') => {
    if (!password.trim()) return;
    
    try {
      const { data: users, error } = await supabase
        .from(table)
        .select('user_id, password')
        .eq('user_id', password);

      if (error || !users || users.length === 0) {
        setVerifyFromDB('No user found in ' + table);
        setDbMatch(null);
        return;
      }

      const userData = users[0] as { user_id: string; password?: string; access_key?: string };
      const storedKey = table === 'user_master' ? userData.password! : userData.access_key!;
      const [salt] = storedKey.split(':');
      const hashed = await hashPassword(password, salt);
      
      const match = hashed === storedKey;
      setVerifyFromDB(match ? '✓ MATCH!' : '✗ NO MATCH');
      setDbMatch({ user: userData.user_id, match });
    } catch (err) {
      setVerifyFromDB('Error: ' + err);
    }
  };

  return (
    <Layout>
      <div style={{ padding: '40px 5%', maxWidth: '600px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '10px' }}>🔐 Password Hash Generator</h1>
        <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
          Generate PBKDF2-SHA256 hashed passwords for database storage. Format: salt:hash
        </p>

        <div style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px' }}>
          <div className="form-group">
            <label className="form-label">Password to Hash</label>
            <input
              className="form-control"
              type="text"
              placeholder="Enter password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>

          <button
            className="btn btn-blue"
            onClick={handleGenerate}
            disabled={!password.trim()}
            style={{ marginTop: '10px' }}
          >
            Generate Hash
          </button>

          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="useCustomSalt"
              checked={useCustomSalt}
              onChange={e => setUseCustomSalt(e.target.checked)}
            />
            <label htmlFor="useCustomSalt" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>Use custom salt (for verification)</label>
          </div>

          {useCustomSalt && (
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label className="form-label">Custom Salt (32 hex chars)</label>
              <input
                className="form-control"
                type="text"
                placeholder="Enter existing salt to verify hash"
                value={customSalt}
                onChange={e => setCustomSalt(e.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
            </div>
          )}
        </div>

        {hash && (
          <div style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '16px' }}>Generated Hash</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label">Salt (16 bytes hex)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="form-control"
                  type="text"
                  value={salt}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                <button
                  className="btn btn-outline-gray btn-sm"
                  onClick={() => copyToClipboard(salt)}
                >
                  {copied ? '✓' : 'Copy'}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="form-label">Full Hash (salt:hash)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="form-control"
                  type="text"
                  value={hash}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                <button
                  className="btn btn-outline-gray btn-sm"
                  onClick={() => copyToClipboard(hash)}
                >
                  {copied ? '✓' : 'Copy'}
                </button>
              </div>
            </div>

            <div style={{ background: 'var(--blue-pale)', border: '1px solid var(--blue-soft)', borderRadius: 'var(--radius-sm)', padding: '12px', fontSize: '0.85rem', color: 'var(--blue)' }}>
              <strong>SQL for user_access table:</strong><br />
              <code style={{ display: 'block', marginTop: '8px', fontFamily: 'monospace' }}>
                INSERT INTO user_access (user_id, access_key) VALUES ('username', '{hash}');
              </code>
            </div>
          </div>
        )}

        <div style={{ marginTop: '24px', padding: '16px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
          <strong>Technical Details:</strong>
          <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
            <li>Algorithm: PBKDF2 with SHA-256</li>
            <li>Iterations: 100,000</li>
            <li>Salt: 16 bytes (32 hex characters)</li>
            <li>Hash: 32 bytes (64 hex characters)</li>
            <li>Total length: 96 characters</li>
          </ul>
        </div>

        {/* Verify from Database */}
        {hash && (
          <div style={{ marginTop: '24px', background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '16px' }}>🔍 Verify Against Database</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginBottom: '16px' }}>
              Check if this password matches an existing user in the database.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button className="btn btn-blue btn-sm" onClick={() => handleVerifyFromDB('user_master')}>
                Verify in user_master
              </button>
              <button className="btn btn-orange btn-sm" onClick={() => handleVerifyFromDB('user_access')}>
                Verify in user_access
              </button>
            </div>
            {verifyFromDB && (
              <div style={{ marginTop: '12px', padding: '12px', background: dbMatch?.match ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', border: '1px solid ' + (dbMatch?.match ? '#22c55e' : '#ef4444'), borderRadius: 'var(--radius-sm)', fontWeight: 700, color: dbMatch?.match ? '#16a34a' : '#dc2626' }}>
                {verifyFromDB}
                {dbMatch && <span style={{ marginLeft: '8px' }}>User: {dbMatch.user}</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HashGeneratorPage;
